import { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { ApiJobFunction } from 'api/types/ApiJobFunction';
import { ApiJobSynonymFamilyId } from 'api/types/ApiTypedId';
import { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { searchAlgoliaOptionsLocationData } from 'modules/search/algolia/options/searchAlgoliaOptionsLocationData';
import { SearchLocation } from 'modules/search/types/SearchLocation';

type Args = {
  job: ApiJob;
  userSearchLocation: SearchLocation | undefined;
};

export function searchAlgoliaGetSimilarJobsOptions({
  job,
  userSearchLocation,
}: Args): {
  filters: {
    jobFunction?: ApiJobFunction[];
    areaOfFocus?: ApiAreaOfFocus[];
    familyId?: ApiJobSynonymFamilyId;
  };
  locationOptions: Record<string, unknown>;
} {
  const { synonym, functions, org } = job;

  const locationOptions =
    userSearchLocation?.latitude && userSearchLocation?.longitude
      ? searchAlgoliaOptionsLocationData('auto', userSearchLocation)
          .locationOptions
      : {};

  // 1. If the listing the person landed on is a job with a synonym assigned, search for the job family ID of that synonym in the location that is set in the user's browser.
  if (synonym)
    return { filters: { familyId: synonym.familyId }, locationOptions };

  // 2. If no synonym is assigned, search results displayed should be related to the job functions/skills set for the posted listing, in the location that is set in the user's browser.
  if (functions.length > 0) {
    return { filters: { jobFunction: functions }, locationOptions };
  }

  // 3. If no job functions/skills are set, fall back to a search for the areas of focus for the organization (if org type is Nonprofit, Government, or Business), in the location that is set in the user's browser.
  if (
    org &&
    ['NONPROFIT', 'GOVERNMENT', 'CORPORATION'].includes(org.orgType) &&
    org.areasOfFocusKeys.length > 0
  ) {
    return {
      filters: { areaOfFocus: org.areasOfFocusKeys },
      locationOptions,
    };
  }

  // If no areas of focus are set on the organization (or if it is posted by a Consultant or Recruiter Org), fall back to a search for all listings in the location that is set in the user's browser.
  return { filters: {}, locationOptions };
}
